import React, { useEffect, useState } from "react";
import { http } from "../lib/api";
import { TIPOS_SEGURO_B2B, TIPOS_SEGURO_B2C, ESTADOS_POLIZA } from "../lib/constants";
import { Plus, Trash2, Save, X, Edit3 } from "lucide-react";
import { toast } from "sonner";

const emptyCliente = {
  tipo: "comunidad", nombre: "", rut: "", email: "", telefono: "",
  direccion: "", comuna: "", contacto_nombre: "", contacto_rol: "",
  notas: "", polizas: [],
};
const emptyPoliza = {
  tipo_seguro: "incendio_sismo", compania: "", numero_poliza: "",
  prima_anual_uf: 0, fecha_inicio: "", fecha_renovacion: "",
  estado: "vigente", notas: "",
};

export default function AdminClientes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // cliente being edited or null
  const [isNew, setIsNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/clientes");
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...emptyCliente }); setIsNew(true); };
  const openEdit = (c) => { setEditing({ ...c, polizas: c.polizas || [] }); setIsNew(false); };
  const close = () => { setEditing(null); setIsNew(false); };

  const save = async () => {
    try {
      if (isNew) {
        const payload = { ...editing, polizas: editing.polizas || [] };
        await http.post("/admin/clientes", payload);
        toast.success("Cliente creado");
      } else {
        const payload = { ...editing };
        delete payload.id; delete payload.created_at; delete payload.updated_at;
        await http.patch(`/admin/clientes/${editing.id}`, payload);
        toast.success("Cliente actualizado");
      }
      close(); load();
    } catch (e) {
      toast.error("Error al guardar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar cliente?")) return;
    try { await http.delete(`/admin/clientes/${id}`); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  return (
    <div data-testid="admin-clientes">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Clientes & Pólizas</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Carga clientes aprobados y gestiona sus pólizas.</p>
        </div>
        <button className="btn-primary" onClick={openNew} data-testid="btn-new-cliente"><Plus size={16}/>Nuevo cliente</button>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: "var(--border-subtle)" }}>
        {loading ? <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Cargando…</div>
         : items.length === 0 ? <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>No hay clientes cargados. Crea uno para empezar.</div>
         : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead><tr><th>Cliente</th><th>Tipo</th><th>RUT</th><th>Pólizas</th><th>Estado</th><th></th></tr></thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id}>
                    <td className="font-semibold">{c.nombre}<div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{c.email}</div></td>
                    <td className="capitalize">{c.tipo}</td>
                    <td className="text-[13px]">{c.rut}</td>
                    <td>{(c.polizas || []).length}</td>
                    <td><span className={`badge badge--${c.estado || "activo"}`}>{c.estado || "activo"}</span></td>
                    <td className="flex gap-2">
                      <button onClick={() => openEdit(c)} title="Editar" style={{ color: "var(--brand-accent)" }} data-testid={`edit-${c.id}`}><Edit3 size={15}/></button>
                      <button onClick={() => remove(c.id)} title="Eliminar" style={{ color: "#D32F2F" }}><Trash2 size={15}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <ClienteModal cliente={editing} isNew={isNew} setCliente={setEditing} onClose={close} onSave={save}/>}
    </div>
  );
}

function ClienteModal({ cliente, setCliente, onClose, onSave, isNew }) {
  const set = (k, v) => setCliente((p) => ({ ...p, [k]: v }));
  const addPoliza = () => setCliente((p) => ({ ...p, polizas: [...(p.polizas || []), { ...emptyPoliza, id: crypto.randomUUID() }] }));
  const updatePoliza = (i, k, v) => setCliente((p) => {
    const polizas = [...p.polizas];
    polizas[i] = { ...polizas[i], [k]: v };
    return { ...p, polizas };
  });
  const removePoliza = (i) => setCliente((p) => ({ ...p, polizas: p.polizas.filter((_, j) => j !== i) }));

  const tiposSeguro = cliente.tipo === "residente" ? TIPOS_SEGURO_B2C : TIPOS_SEGURO_B2B;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10,37,64,0.55)" }} onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="cliente-modal">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10" style={{ borderColor: "var(--border-subtle)" }}>
          <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>{isNew ? "Nuevo cliente" : "Editar cliente"}</h3>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="p-5 space-y-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-3" style={{ color: "var(--brand-navy)" }}>Datos básicos</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Fld label="Tipo" testid="cli-tipo">
                <select className="field-input" value={cliente.tipo} onChange={(e) => set("tipo", e.target.value)}>
                  <option value="comunidad">Comunidad</option>
                  <option value="residente">Residente</option>
                </select>
              </Fld>
              <Fld label="Estado">
                <select className="field-input" value={cliente.estado || "activo"} onChange={(e) => set("estado", e.target.value)}>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </Fld>
              <Fld label="Nombre / Razón social" testid="cli-nombre"><input className="field-input" value={cliente.nombre} onChange={(e) => set("nombre", e.target.value)}/></Fld>
              <Fld label="RUT"><input className="field-input" value={cliente.rut} onChange={(e) => set("rut", e.target.value)}/></Fld>
              <Fld label="Email"><input className="field-input" type="email" value={cliente.email || ""} onChange={(e) => set("email", e.target.value)}/></Fld>
              <Fld label="Teléfono"><input className="field-input" value={cliente.telefono || ""} onChange={(e) => set("telefono", e.target.value)}/></Fld>
              <Fld label="Dirección"><input className="field-input" value={cliente.direccion || ""} onChange={(e) => set("direccion", e.target.value)}/></Fld>
              <Fld label="Comuna"><input className="field-input" value={cliente.comuna || ""} onChange={(e) => set("comuna", e.target.value)}/></Fld>
              <Fld label="Contacto nombre"><input className="field-input" value={cliente.contacto_nombre || ""} onChange={(e) => set("contacto_nombre", e.target.value)}/></Fld>
              <Fld label="Contacto rol"><input className="field-input" value={cliente.contacto_rol || ""} onChange={(e) => set("contacto_rol", e.target.value)} placeholder="Administrador, Presidente..."/></Fld>
            </div>
            <div className="mt-4">
              <Fld label="Notas"><textarea rows={2} className="field-input" value={cliente.notas || ""} onChange={(e) => set("notas", e.target.value)}/></Fld>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: "var(--brand-navy)" }}>Pólizas</div>
              <button className="btn-outline" style={{ padding: "0.4rem 0.75rem", fontSize: 13 }} onClick={addPoliza} data-testid="btn-add-poliza"><Plus size={14}/>Agregar póliza</button>
            </div>
            {(cliente.polizas || []).length === 0 ? (
              <div className="text-sm p-4 border rounded-lg text-center" style={{ borderColor: "var(--border-subtle)", color: "var(--text-muted)" }}>Sin pólizas. Agrega la primera.</div>
            ) : (
              <div className="space-y-3">
                {cliente.polizas.map((p, i) => (
                  <div key={p.id || i} className="border rounded-lg p-4 space-y-3" style={{ borderColor: "var(--border-subtle)" }} data-testid={`poliza-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-bold text-sm" style={{ fontFamily: "Manrope" }}>Póliza #{i + 1}</div>
                      <button onClick={() => removePoliza(i)} style={{ color: "#D32F2F" }}><Trash2 size={14}/></button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <Fld label="Tipo seguro">
                        <select className="field-input" value={p.tipo_seguro} onChange={(e) => updatePoliza(i, "tipo_seguro", e.target.value)}>
                          {tiposSeguro.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </Fld>
                      <Fld label="Compañía"><input className="field-input" value={p.compania} onChange={(e) => updatePoliza(i, "compania", e.target.value)} placeholder="BCI Seguros, Chubb..."/></Fld>
                      <Fld label="Nº póliza"><input className="field-input" value={p.numero_poliza} onChange={(e) => updatePoliza(i, "numero_poliza", e.target.value)}/></Fld>
                      <Fld label="Prima anual (UF)"><input type="number" step="0.01" className="field-input" value={p.prima_anual_uf} onChange={(e) => updatePoliza(i, "prima_anual_uf", parseFloat(e.target.value) || 0)}/></Fld>
                      <Fld label="Fecha inicio"><input type="date" className="field-input" value={p.fecha_inicio} onChange={(e) => updatePoliza(i, "fecha_inicio", e.target.value)}/></Fld>
                      <Fld label="Fecha renovación"><input type="date" className="field-input" value={p.fecha_renovacion} onChange={(e) => updatePoliza(i, "fecha_renovacion", e.target.value)}/></Fld>
                      <Fld label="Estado">
                        <select className="field-input" value={p.estado} onChange={(e) => updatePoliza(i, "estado", e.target.value)}>
                          {ESTADOS_POLIZA.map((e) => <option key={e} value={e}>{e}</option>)}
                        </select>
                      </Fld>
                    </div>
                    <Fld label="Notas"><input className="field-input" value={p.notas || ""} onChange={(e) => updatePoliza(i, "notas", e.target.value)}/></Fld>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-3 sticky bottom-0 bg-white" style={{ borderColor: "var(--border-subtle)" }}>
          <button className="btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={onSave} data-testid="btn-save-cliente"><Save size={16}/>Guardar</button>
        </div>
      </div>
    </div>
  );
}

const Fld = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="field-label">{label}</label>
    {children}
  </div>
);
